/**
 * Boîte de réception v2 (lot 3, 2026-10-28) — appels /inbox, /conversations/:id/{takeover,release,send},
 * réponses enregistrées, fiche contact (STOP, étiquettes, champs, RGPD), export CSV.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, getToken, getWorkspaceId } from '@/lib/api';

export type InboxFilter = 'all' | 'unread' | 'to_answer' | 'scenario' | 'takeover' | 'overdue' | 'closed';
export interface InboxRow {
  id: string; prospect_id: string; prospect_display_name: string | null; prospect_handle: string | null; channel: string; status: string;
  partner_unread_count: number; last_inbound_at: string | null; last_outbound_at: string | null; updated_at: string;
  participant_name: string | null; participant_username: string | null; participant_avatar: string | null;
  assigned_to: string | null; assigned_to_name: string | null; human_takeover_at: string | null; copilot_mode_active: boolean; inea_paused_by_partner: boolean;
  to_answer: boolean; active_run_id: string | null; active_run_status: string | null; active_run_node: string | null; active_run_template: string | null;
  opted_out: boolean; tags: string[]; reply_due_at: string | null; overdue: boolean;
}
export interface InboxTranscriptItem { id: string; direction: 'inbound' | 'outbound' | 'system'; kind: string; content: string | null; meta: Record<string, unknown>; occurred_at: string; automation_name: string | null }
export interface InboxContact { prospect_id: string; display_name: string | null; handle: string | null; platform: string | null; stage: string; score: number | null; fields: Record<string, unknown>; tags: string[]; opted_out_at: string | null; source: string; source_keyword: string | null; source_post_id: string | null; source_template_name: string | null; source_account_username: string | null; anonymized_at: string | null }
export interface InboxMember { user_id: string; name: string; role: string }
export interface InboxDetail { conversation: InboxRow & { window_open: boolean; window_open_until: string | null }; transcript: InboxTranscriptItem[]; contact: InboxContact; members: InboxMember[] }
export interface SavedReply { id: string; shortcut: string; title: string; body: string; buttons: Array<{ title: string; url: string }>; updated_at: string }

export const NETWORK_CHANNELS = new Set(['instagram', 'facebook', 'linkedin', 'tiktok', 'youtube', 'twitter', 'threads', 'reddit', 'pinterest', 'bluesky', 'googlebusiness', 'snapchat', 'discord', 'slack', 'whatsapp']);

export function useInbox(filter: InboxFilter, assigned: string | null) {
  const qs = new URLSearchParams({ filter });
  if (assigned) qs.set('assigned', assigned);
  return useQuery({ queryKey: ['inbox', filter, assigned], queryFn: () => apiRequest<{ conversations: InboxRow[] }>(`/inbox?${qs.toString()}`), refetchInterval: 30_000 });
}

export function useInboxDetail(id: string | null) {
  return useQuery({ queryKey: ['inbox', 'one', id], queryFn: () => apiRequest<InboxDetail>(`/inbox/${id}`), enabled: !!id, refetchInterval: 15_000 });
}

function useInvalidate() {
  const qc = useQueryClient();
  return (id?: string) => {
    void qc.invalidateQueries({ queryKey: ['inbox'] });
    if (id) void qc.invalidateQueries({ queryKey: ['inbox', 'one', id] });
    void qc.invalidateQueries({ queryKey: ['conversations'] });
  };
}

export function useConversationCommand() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, command, body }: { id: string; command: 'takeover' | 'release' | 'send'; body?: unknown }) => apiRequest<unknown>(`/conversations/${id}/${command}`, { method: 'POST', body: body ?? {} }),
    onSuccess: (_r, v) => invalidate(v.id),
  });
}

export function useAssign() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: ({ id, userId }: { id: string; userId: string | null }) => apiRequest<{ ok: true }>(`/inbox/${id}/assign`, { method: 'POST', body: { user_id: userId } }), onSuccess: (_r, v) => invalidate(v.id) });
}

export function useSavedReplies() {
  return useQuery({ queryKey: ['inbox', 'saved-replies'], queryFn: () => apiRequest<{ replies: SavedReply[] }>('/inbox/saved-replies'), staleTime: 60_000 });
}
export function useSaveReply() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id?: string; shortcut: string; title: string; body: string; buttons: Array<{ title: string; url: string }> }) =>
      input.id ? apiRequest<{ reply: SavedReply }>(`/inbox/saved-replies/${input.id}`, { method: 'PUT', body: input }) : apiRequest<{ reply: SavedReply }>('/inbox/saved-replies', { method: 'POST', body: input }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['inbox', 'saved-replies'] }),
  });
}
export function useDeleteReply() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => apiRequest<void>(`/inbox/saved-replies/${id}`, { method: 'DELETE' }), onSuccess: () => void qc.invalidateQueries({ queryKey: ['inbox', 'saved-replies'] }) });
}

export function useContactCommand() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ prospectId, command, body }: { prospectId: string; command: 'stop' | 'unstop' | 'anonymize' | 'patch'; body?: unknown; conversationId?: string }) =>
      command === 'patch' ? apiRequest<{ ok: true }>(`/inbox/prospects/${prospectId}`, { method: 'PATCH', body }) : apiRequest<{ ok: true }>(`/inbox/prospects/${prospectId}/${command}`, { method: 'POST' }),
    onSuccess: (_r, v) => invalidate(v.conversationId),
  });
}

/** Export CSV avec le jeton et l'espace courant (un lien nu ne porterait pas l'en-tête). */
export async function downloadProspectsCsv(params: Record<string, string> = {}): Promise<void> {
  const base = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:4007';
  const headers: Record<string, string> = { Authorization: `Bearer ${getToken() ?? ''}` };
  const ws = getWorkspaceId();
  if (ws) headers['X-Workspace'] = ws;
  const qs = new URLSearchParams(params).toString();
  const res = await fetch(`${base}/inbox/prospects/export.csv${qs ? `?${qs}` : ''}`, { headers });
  if (!res.ok) throw new Error(`export ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `prospects-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
